import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import SearchSelect from '../components/ui/SearchSelect';
import { toast } from '../lib/toast';
import { parseMarkdown } from '../lib/markdown';
import Sidebar from '../components/layout/Sidebar';
import { Plus, Search, Trash2, Edit2, Package, X, ChevronRight, FolderTree, FlaskConical, Camera, Upload, Image, Clock, ChefHat, ListOrdered, Eye, Layers, Scale, Check, Building2, FileDown } from 'lucide-react';

// Simple Modal Component
const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      <div className={`relative bg-background rounded-lg shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} mx-4 p-6 z-10 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-chivo font-extrabold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Products = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz, branchName, branches } = useBranch();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [processedSupplies, setProcessedSupplies] = useState([]);
  const [recipes, setRecipes] = useState({});
  const [areas, setAreas] = useState([]); // For deduction area selection
  const [extraGroups, setExtraGroups] = useState([]); // Grupos de extras disponibles
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Product Dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', cost: '', category_id: '', stock: '', is_available: true, image_url: '', deduction_area_id: '', kitchen_station: '', sold_by_weight: false, weight_unit: 'kg',
    // Elaboration details
    prep_time_minutes: '', cook_time_minutes: '', presentation: '', preparation_steps: [],
    // Selectable ingredients
    has_selectable_ingredients: false, total_weight_grams: '', selectable_ingredients: [],
    // Branch assignment
    branch_id: '',
    // Digital Menu visibility
    visible_in_menu: true,
    is_popular: false,
    // Promo only - Solo para promociones
    promo_only: false,
    // Grupos de extras asignados
    extra_group_ids: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // Tab state for product form
  const [productFormTab, setProductFormTab] = useState('general');
  
  // Recipe Dialog
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [recipeProduct, setRecipeProduct] = useState(null);
  const [recipeIngredients, setRecipeIngredients] = useState([]);

  // Category Dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ 
    name: '', 
    description: '', 
    color: '#f97316',
    parent_id: '',
    kitchen_station: ''
  });

  useEffect(() => {
    fetchData();
  }, [currentBranch]); // Reload when branch changes

  const fetchData = async () => {
    try {
      // OPTIMIZED: Single API call for all product data
      const branchParams = currentBranch 
        ? `?branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}` 
        : '';
      
      const res = await api.get(`/products/init-data${branchParams}`);
      const data = res.data;
      
      setProducts(data.products);
      setCategories(data.categories);
      setFlatCategories(data.flat_categories);
      setSupplies(data.supplies);
      setProcessedSupplies(data.processed_supplies);
      setAreas(data.areas);
      setExtraGroups(data.extra_groups || []);
      setRecipes(data.recipes);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Category Functions
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...categoryForm,
        parent_id: categoryForm.parent_id || null,
        // Branch assignment - for new categories assign current branch (not for Matriz)
        branch_id: editingCategory ? editingCategory.branch_id : (isMatriz ? null : currentBranch?.branch_id)
      };
      
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.category_id}`, payload);
        toast.success('Categoría actualizada');
      } else {
        await api.post('/categories', payload);
        toast.success('Categoría creada');
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      description: cat.description || '',
      color: cat.color || '#f97316',
      parent_id: cat.parent_id || '',
      kitchen_station: cat.kitchen_station || ''
    });
    setCategoryDialogOpen(true);
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', description: '', color: '#f97316', parent_id: '', kitchen_station: '' });
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('¿Eliminar esta categoría y sus subcategorías?')) return;
    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success('Categoría eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar categoría');
    }
  };

  // Product Functions
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        cost: parseFloat(productForm.cost) || 0,
        stock: parseInt(productForm.stock) || 0,
        // Elaboration details
        prep_time_minutes: productForm.prep_time_minutes ? parseInt(productForm.prep_time_minutes) : null,
        cook_time_minutes: productForm.cook_time_minutes ? parseInt(productForm.cook_time_minutes) : null,
        presentation: productForm.presentation || null,
        preparation_steps: productForm.preparation_steps.length > 0 ? productForm.preparation_steps : null,
        // Selectable ingredients
        has_selectable_ingredients: productForm.has_selectable_ingredients,
        total_weight_grams: productForm.total_weight_grams ? parseFloat(productForm.total_weight_grams) : null,
        selectable_ingredients: productForm.has_selectable_ingredients ? productForm.selectable_ingredients : [],
        // Branch assignment - for new products assign current branch (not for Matriz)
        branch_id: editingProduct ? (productForm.branch_id || editingProduct.branch_id) : (isMatriz ? null : currentBranch?.branch_id),
        // Grupos de extras
        extra_group_ids: productForm.extra_group_ids || []
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.product_id}`, data);
        toast.success('Producto actualizado');
      } else {
        await api.post('/products', data);
        toast.success('Producto creado');
      }
      
      setProductDialogOpen(false);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      cost: product.cost?.toString() || '',
      category_id: product.category_id,
      stock: product.stock.toString(),
      is_available: product.is_available,
      image_url: product.image_url || '',
      deduction_area_id: product.deduction_area_id || '',
      kitchen_station: product.kitchen_station || '',
      sold_by_weight: product.sold_by_weight || false,
      weight_unit: product.weight_unit || 'kg',
      // Elaboration details
      prep_time_minutes: product.prep_time_minutes?.toString() || '',
      cook_time_minutes: product.cook_time_minutes?.toString() || '',
      presentation: product.presentation || '',
      preparation_steps: product.preparation_steps || [],
      // Selectable ingredients
      has_selectable_ingredients: product.has_selectable_ingredients || false,
      total_weight_grams: product.total_weight_grams?.toString() || '',
      selectable_ingredients: product.selectable_ingredients || [],
      // Branch assignment
      branch_id: product.branch_id || '',
      // Digital Menu
      visible_in_menu: product.visible_in_menu !== false,
      is_popular: product.is_popular || false,
      promo_only: product.promo_only || false,
      // Grupos de extras
      extra_group_ids: product.extra_group_ids || []
    });
    setProductFormTab('general');
    setProductDialogOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await api.delete(`/products/${productId}`);
      toast.success('Producto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleDownloadProductPdf = async (product) => {
    try {
      toast.info('Generando ficha PDF...');
      const response = await api.get(`/catalog-reports/product-pdf/${product.product_id}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = product.name.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_').slice(0, 50);
      link.setAttribute('download', `ficha_${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ficha PDF descargada');
    } catch (error) {
      toast.error('Error al generar PDF');
    }
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: '', description: '', price: '', cost: '', category_id: '', stock: '', is_available: true, image_url: '', deduction_area_id: '', kitchen_station: '', sold_by_weight: false, weight_unit: 'kg',
      prep_time_minutes: '', cook_time_minutes: '', presentation: '', preparation_steps: [],
      has_selectable_ingredients: false, total_weight_grams: '', selectable_ingredients: [],
      branch_id: '',
      visible_in_menu: true,
      is_popular: false,
      promo_only: false,
      extra_group_ids: []
    });
    setProductFormTab('general');
  };

  // Selectable ingredients helpers
  const addSelectableIngredient = (processedSupply) => {
    const exists = productForm.selectable_ingredients.find(i => i.processed_id === processedSupply.processed_id);
    if (exists) {
      toast.error('Este ingrediente ya está agregado');
      return;
    }
    
    // Calculate base quantity from total weight divided by current count + 1
    const totalWeight = parseFloat(productForm.total_weight_grams) || 0;
    const newCount = productForm.selectable_ingredients.length + 1;
    const baseQuantity = totalWeight > 0 ? Math.round(totalWeight / newCount) : 50;
    
    setProductForm(prev => ({
      ...prev,
      selectable_ingredients: [...prev.selectable_ingredients, {
        processed_id: processedSupply.processed_id,
        name: processedSupply.name,
        base_quantity: baseQuantity,
        is_default: true
      }]
    }));
  };

  const removeSelectableIngredient = (processedId) => {
    setProductForm(prev => ({
      ...prev,
      selectable_ingredients: prev.selectable_ingredients.filter(i => i.processed_id !== processedId)
    }));
  };

  const updateSelectableIngredient = (processedId, field, value) => {
    setProductForm(prev => ({
      ...prev,
      selectable_ingredients: prev.selectable_ingredients.map(i => 
        i.processed_id === processedId ? { ...i, [field]: value } : i
      )
    }));
  };

  const recalculateBaseQuantities = () => {
    const totalWeight = parseFloat(productForm.total_weight_grams) || 0;
    const count = productForm.selectable_ingredients.length;
    if (count === 0 || totalWeight === 0) return;
    
    const baseQuantity = Math.round(totalWeight / count);
    setProductForm(prev => ({
      ...prev,
      selectable_ingredients: prev.selectable_ingredients.map(i => ({
        ...i,
        base_quantity: baseQuantity
      }))
    }));
  };

  // Preparation steps helpers
  const addProductStep = () => {
    setProductForm(prev => ({
      ...prev,
      preparation_steps: [...prev.preparation_steps, {
        step_number: prev.preparation_steps.length + 1,
        description: '',
        duration_minutes: null
      }]
    }));
  };

  const updateProductStep = (index, field, value) => {
    setProductForm(prev => ({
      ...prev,
      preparation_steps: prev.preparation_steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const removeProductStep = (index) => {
    setProductForm(prev => ({
      ...prev,
      preparation_steps: prev.preparation_steps
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, step_number: i + 1 }))
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for slow connections
      });
      
      if (response.data && response.data.url) {
        setProductForm(prev => ({ ...prev, image_url: response.data.url }));
        toast.success('Imagen subida correctamente');
      } else {
        toast.error('Error: no se recibió la URL de la imagen');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Tiempo de espera agotado. Intenta con una imagen más pequeña.');
      } else if (error.response?.status === 413) {
        toast.error('La imagen es demasiado grande');
      } else {
        toast.error(error.response?.data?.detail || 'Error al subir imagen. Intenta de nuevo.');
      }
    } finally {
      setUploadingImage(false);
      // Reset the file input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Recipe Functions
  const openRecipeDialog = (product) => {
    setRecipeProduct(product);
    const existingRecipe = recipes[product.product_id];
    if (existingRecipe) {
      setRecipeIngredients(existingRecipe.ingredients.map(ing => ({
        type: ing.type || 'basic',
        supply_id: ing.supply_id || '',
        processed_id: ing.processed_id || '',
        quantity: ing.quantity.toString()
      })));
    } else {
      setRecipeIngredients([]);
    }
    setRecipeDialogOpen(true);
  };

  const addIngredientRow = () => {
    setRecipeIngredients([...recipeIngredients, { type: 'basic', supply_id: '', processed_id: '', quantity: '' }]);
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...recipeIngredients];
    updated[index][field] = value;
    // Clear the other ID when switching types
    if (field === 'supply_id') {
      updated[index].processed_id = '';
    } else if (field === 'processed_id') {
      updated[index].supply_id = '';
    }
    setRecipeIngredients(updated);
  };

  const removeIngredient = (index) => {
    setRecipeIngredients(recipeIngredients.filter((_, i) => i !== index));
  };

  const handleRecipeSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty ingredients
    const validIngredients = recipeIngredients.filter(ing => 
      (ing.supply_id || ing.processed_id) && ing.quantity
    );
    
    if (validIngredients.length === 0) {
      toast.error('Agrega al menos un insumo');
      return;
    }
    
    try {
      await api.post('/recipes', {
        product_id: recipeProduct.product_id,
        ingredients: validIngredients.map(ing => ({
          supply_id: ing.supply_id || null,
          processed_id: ing.processed_id || null,
          quantity: parseFloat(ing.quantity)
        }))
      });
      toast.success('Receta guardada');
      setRecipeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar receta');
    }
  };

  const calculateRecipeCost = () => {
    return recipeIngredients.reduce((total, ing) => {
      let costPerUnit = 0;
      if (ing.supply_id) {
        const supply = supplies.find(s => s.supply_id === ing.supply_id);
        costPerUnit = supply?.cost_per_unit || 0;
      } else if (ing.processed_id) {
        const processed = processedSupplies.find(p => p.processed_id === ing.processed_id);
        costPerUnit = processed?.cost_per_unit || 0;
      }
      if (ing.quantity) {
        return total + (costPerUnit * parseFloat(ing.quantity));
      }
      return total;
    }, 0);
  };

  // Get all category IDs including subcategories for filtering (recursive)
  const getCategoryIdsWithChildren = (categoryId) => {
    const ids = [categoryId];
    const addChildren = (parentId) => {
      flatCategories.forEach(c => {
        if (c.parent_id === parentId) {
          ids.push(c.category_id);
          addChildren(c.category_id); // Recursivo para sub-subcategorías
        }
      });
    };
    addChildren(categoryId);
    return ids;
  };

  // Contar productos en una categoría incluyendo todas sus subcategorías
  const getProductCountForCategory = (categoryId) => {
    const categoryIds = getCategoryIdsWithChildren(categoryId);
    return products.filter(p => categoryIds.includes(p.category_id)).length;
  };

  // Filtering
  const filteredProducts = products.filter(p => {
    let matchesCategory = selectedCategory === 'all';
    if (!matchesCategory) {
      const categoryIds = getCategoryIdsWithChildren(selectedCategory);
      matchesCategory = categoryIds.includes(p.category_id);
    }
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryName = (categoryId) => {
    const cat = flatCategories.find(c => c.category_id === categoryId);
    if (!cat) return 'Sin categoría';
    
    if (cat.parent_id) {
      const parent = flatCategories.find(c => c.category_id === cat.parent_id);
      return parent ? `${parent.name} > ${cat.name}` : cat.name;
    }
    return cat.name;
  };

  const getParentCategories = () => {
    return flatCategories.filter(c => !c.parent_id);
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-chivo font-extrabold" data-testid="products-title">
                Catálogo de Productos
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>{products.length} productos en {flatCategories.length} categorías</span>
                {currentBranch && (
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isMatriz ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    <Building2 className="w-3 h-3" />
                    {isMatriz ? 'Vista Global (Matriz)' : branchName}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  resetCategoryForm();
                  setCategoryDialogOpen(true);
                }}
                data-testid="add-category-btn"
              >
                <FolderTree className="w-4 h-4 mr-2" />
                Nueva Categoría
              </Button>

              <Button 
                variant="outline"
                onClick={async () => {
                  try {
                    toast.info('Generando catálogo PDF...');
                    const response = await api.get('/catalog-reports/catalog-pdf?include_recipes=true', {
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `catalogo_productos_${new Date().toISOString().split('T')[0]}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Catálogo descargado');
                  } catch (error) {
                    toast.error('Error al generar catálogo');
                    console.error(error);
                  }
                }}
                data-testid="download-catalog-btn"
              >
                <Package className="w-4 h-4 mr-2" />
                Descargar Catálogo PDF
              </Button>

              <Button 
                className="bg-accent text-white hover:bg-accent/90"
                onClick={() => {
                  resetProductForm();
                  setProductDialogOpen(true);
                }}
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Producto
              </Button>
            </div>
          </div>

          {/* Two Panel Layout */}
          <div className="flex gap-6">
            {/* Left Panel - Categories */}
            <div className="w-72 flex-shrink-0">
              <div className="sticky top-4 bg-white border rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4">
                  <h2 className="font-bold flex items-center gap-2">
                    <FolderTree className="w-5 h-5" />
                    Categorías
                  </h2>
                </div>
                <div className="p-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {/* All Products */}
                  <button
                    className={`w-full text-left px-4 py-3 rounded-lg mb-1 transition-all flex items-center justify-between ${
                      selectedCategory === 'all' 
                        ? 'bg-accent text-white font-semibold shadow-md' 
                        : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setSelectedCategory('all')}
                  >
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Todos
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === 'all' ? 'bg-white/20' : 'bg-gray-200'}`}>
                      {products.length}
                    </span>
                  </button>
                  
                  <div className="border-t my-2"></div>
                  
                  {/* Categories Tree */}
                  {categories.map(cat => {
                    const catCount = getProductCountForCategory(cat.category_id);
                    const isSelected = selectedCategory === cat.category_id;
                    const catIsCaliente = cat.kitchen_station === 'caliente';
                    const catIsFria = cat.kitchen_station === 'fria';
                    
                    return (
                      <div key={cat.category_id} className="mb-1">
                        {/* Parent Category */}
                        <div className="group flex items-center">
                          <button
                            className={`flex-1 text-left px-4 py-2.5 rounded-lg transition-all flex items-center justify-between ${
                              isSelected 
                                ? 'bg-accent text-white font-semibold shadow-md' 
                                : 'hover:bg-gray-100 font-medium'
                            }`}
                            onClick={() => setSelectedCategory(cat.category_id)}
                          >
                            <span className="flex items-center gap-2">
                              {catIsCaliente ? '🔥' : catIsFria ? '🧊' : '📁'}
                              <span className="truncate max-w-[120px]">{cat.name}</span>
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-gray-200'}`}>
                              {catCount}
                            </span>
                          </button>
                          <div className="opacity-0 group-hover:opacity-100 flex transition-opacity">
                            <button className="p-1.5 hover:bg-gray-200 rounded" onClick={() => handleEditCategory(cat)}>
                              <Edit2 className="w-3 h-3 text-gray-500" />
                            </button>
                            <button className="p-1.5 hover:bg-red-100 rounded" onClick={() => handleDeleteCategory(cat.category_id)}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Subcategories */}
                        {cat.subcategories?.length > 0 && (
                          <div className="ml-4 mt-1 border-l-2 border-gray-200 pl-2 space-y-0.5">
                            {cat.subcategories.map(subcat => {
                              const subCount = getProductCountForCategory(subcat.category_id);
                              const subSelected = selectedCategory === subcat.category_id;
                              const subIsCaliente = subcat.kitchen_station === 'caliente';
                              const subIsFria = subcat.kitchen_station === 'fria';
                              
                              return (
                                <div key={subcat.category_id}>
                                  <div className="group flex items-center">
                                    <button
                                      className={`flex-1 text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between ${
                                        subSelected 
                                          ? (subIsCaliente ? 'bg-red-500' : subIsFria ? 'bg-blue-500' : 'bg-accent') + ' text-white font-medium shadow' 
                                          : 'hover:bg-gray-100'
                                      }`}
                                      onClick={() => setSelectedCategory(subcat.category_id)}
                                    >
                                      <span className="flex items-center gap-1.5">
                                        {subIsCaliente ? '🔥' : subIsFria ? '🧊' : ''}
                                        <span className="truncate max-w-[100px]">{subcat.name}</span>
                                      </span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${subSelected ? 'bg-white/20' : 'bg-gray-200'}`}>
                                        {subCount}
                                      </span>
                                    </button>
                                    <div className="opacity-0 group-hover:opacity-100 flex transition-opacity">
                                      <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleEditCategory(subcat)}>
                                        <Edit2 className="w-2.5 h-2.5 text-gray-500" />
                                      </button>
                                      <button className="p-1 hover:bg-red-100 rounded" onClick={() => handleDeleteCategory(subcat.category_id)}>
                                        <Trash2 className="w-2.5 h-2.5 text-red-500" />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {/* Sub-subcategories (3rd level) */}
                                  {subcat.subcategories?.length > 0 && (
                                    <div className="ml-4 mt-0.5 border-l border-gray-200 pl-2 space-y-0.5">
                                      {subcat.subcategories.map(subsub => {
                                        const subsubCount = getProductCountForCategory(subsub.category_id);
                                        const subsubSelected = selectedCategory === subsub.category_id;
                                        
                                        return (
                                          <div key={subsub.category_id} className="group flex items-center">
                                            <button
                                              className={`flex-1 text-left px-2 py-1.5 rounded text-xs transition-all flex items-center justify-between ${
                                                subsubSelected 
                                                  ? 'bg-accent text-white font-medium' 
                                                  : 'hover:bg-gray-100 text-gray-700'
                                              }`}
                                              onClick={() => setSelectedCategory(subsub.category_id)}
                                            >
                                              <span className="truncate max-w-[90px]">{subsub.name}</span>
                                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${subsubSelected ? 'bg-white/20' : 'bg-gray-200'}`}>
                                                {subsubCount}
                                              </span>
                                            </button>
                                            <div className="opacity-0 group-hover:opacity-100 flex transition-opacity">
                                              <button className="p-0.5 hover:bg-gray-200 rounded" onClick={() => handleEditCategory(subsub)}>
                                                <Edit2 className="w-2 h-2 text-gray-500" />
                                              </button>
                                              <button className="p-0.5 hover:bg-red-100 rounded" onClick={() => handleDeleteCategory(subsub.category_id)}>
                                                <Trash2 className="w-2 h-2 text-red-500" />
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Panel - Products */}
            <div className="flex-1 min-w-0">
              {/* Search and Count Bar */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar productos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-products"
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{filteredProducts.length}</span> productos
                  {selectedCategory !== 'all' && (
                    <span> en <span className="font-medium">{flatCategories.find(c => c.category_id === selectedCategory)?.name || 'categoría'}</span></span>
                  )}
                </div>
              </div>

              {/* Products Table */}
              <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                {/* Table Header */}
                <div className="bg-gray-50 border-b px-4 py-3 grid grid-cols-12 gap-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  <div className="col-span-1">Img</div>
                  <div className="col-span-4">Producto</div>
                  <div className="col-span-2 text-right">Precio</div>
                  <div className="col-span-2 text-right">Costo</div>
                  <div className="col-span-1 text-right">Margen</div>
                  <div className="col-span-2 text-center">Acciones</div>
                </div>
                
                {/* Table Body */}
                <div className="divide-y max-h-[calc(100vh-280px)] overflow-y-auto">
                  {filteredProducts.map((product, idx) => {
                    const margin = product.price > 0 ? Math.round(((product.price - product.cost) / product.price) * 100) : 0;
                    const marginColor = margin >= 50 ? 'text-green-600 bg-green-50' : margin >= 30 ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50';
                    const recipe = recipes[product.product_id];
                    
                    return (
                      <div 
                        key={product.product_id}
                        className={`px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-gray-50 transition-colors ${!product.is_available ? 'opacity-50 bg-gray-100' : ''}`}
                      >
                        {/* Image */}
                        <div className="col-span-1">
                          {product.image_url ? (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-12 h-12 rounded-lg object-cover border"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {/* Product Name & Info */}
                        <div className="col-span-4 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
                          <p className="text-xs text-gray-500 truncate">{getCategoryName(product.category_id)}</p>
                          {recipe && (
                            <span className="inline-flex items-center gap-1 text-xs text-accent">
                              <FlaskConical className="w-3 h-3" />
                              {recipe.ingredients?.length || 0} ingredientes
                            </span>
                          )}
                        </div>
                        
                        {/* Price */}
                        <div className="col-span-2 text-right">
                          <span className="font-bold text-gray-900">${product.price?.toLocaleString()}</span>
                        </div>
                        
                        {/* Cost */}
                        <div className="col-span-2 text-right">
                          <span className="text-gray-600">${product.cost?.toLocaleString() || '0'}</span>
                        </div>
                        
                        {/* Margin */}
                        <div className="col-span-1 text-right">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${marginColor}`}>
                            {margin}%
                          </span>
                        </div>
                        
                        {/* Actions */}
                        <div className="col-span-2 flex items-center justify-center gap-1">
                          <button
                            className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                            onClick={() => handleDownloadProductPdf(product)}
                            title="Descargar PDF"
                          >
                            <FileDown className="w-4 h-4 text-blue-500" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-accent/10 rounded-lg transition-colors"
                            onClick={() => openRecipeDialog(product)}
                            title="Receta"
                          >
                            <FlaskConical className="w-4 h-4 text-accent" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                            onClick={() => handleEditProduct(product)}
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                            onClick={() => handleDeleteProduct(product.product_id)}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Empty State */}
              {filteredProducts.length === 0 && !loading && (
                <div className="bg-white border rounded-xl p-12 text-center">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-4">No hay productos en esta categoría</p>
                  <Button 
                    className="bg-accent text-white hover:bg-accent/90"
                    onClick={() => {
                      resetProductForm();
                      setProductDialogOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Producto
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Category Modal */}
      <Modal
        open={categoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          resetCategoryForm();
        }}
        title={editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
      >
        <form onSubmit={handleCategorySubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              placeholder="Ej: Mariscos, Bebidas, Postres..."
              required
              data-testid="category-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Categoría Padre (opcional)</Label>
            <select
              value={categoryForm.parent_id}
              onChange={(e) => setCategoryForm({ ...categoryForm, parent_id: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="category-parent-select"
            >
              <option value="">Ninguna (categoría principal)</option>
              {getParentCategories()
                .filter(c => c.category_id !== editingCategory?.category_id)
                .map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {cat.name}
                  </option>
                ))
              }
            </select>
            <p className="text-xs text-muted-foreground">
              Selecciona una categoría padre para crear una subcategoría
            </p>
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Input
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              placeholder="Descripción opcional"
            />
          </div>
          <div className="space-y-2">
            <Label>Estación de Cocina (Monitor)</Label>
            <select
              value={categoryForm.kitchen_station}
              onChange={(e) => setCategoryForm({ ...categoryForm, kitchen_station: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="category-kitchen-station-select"
            >
              <option value="">Sin asignar</option>
              <option value="caliente">🔥 Cocina Caliente</option>
              <option value="fria">🧊 Cocina Fría</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Los productos de esta categoría se enviarán a esta estación del monitor de cocina (si no tienen una estación asignada individualmente).
            </p>
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={categoryForm.color}
                onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                className="w-16 h-10 p-1"
              />
              <Input value={categoryForm.color} readOnly className="flex-1" />
            </div>
          </div>
          <Button type="submit" className="w-full bg-accent text-white hover:bg-accent/90" data-testid="save-category-btn">
            {editingCategory ? 'Actualizar' : 'Crear'} Categoría
          </Button>
        </form>
      </Modal>

      {/* Product Modal */}
      <Modal
        open={productDialogOpen}
        onClose={() => {
          setProductDialogOpen(false);
          resetProductForm();
        }}
        title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        wide
      >
        <form onSubmit={handleProductSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Columna izquierda - Imagen */}
            <div className="space-y-2">
              <Label>Foto del Producto</Label>
              <div 
                className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center cursor-pointer hover:border-accent transition-colors overflow-hidden bg-muted/30"
                onClick={() => fileInputRef.current?.click()}
              >
                {productForm.image_url ? (
                  <div className="relative w-full h-full group">
                    <img 
                      src={productForm.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                  </div>
                ) : uploadingImage ? (
                  <div className="text-center p-4">
                    <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Subiendo...</p>
                  </div>
                ) : (
                  <div className="text-center p-4">
                    <Upload className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Click para subir imagen</p>
                    <p className="text-xs text-muted-foreground/70">JPG, PNG (max 5MB)</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                className="hidden"
              />
              {productForm.image_url && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => setProductForm({ ...productForm, image_url: '' })}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Quitar imagen
                </Button>
              )}
            </div>

            {/* Columna derecha - Datos */}
            <div className="md:col-span-2 space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Producto</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  required
                  placeholder="Ej: Ceviche de Camarón"
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  placeholder="Descripción corta del producto"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio de Venta</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    required
                    placeholder="0.00"
                    data-testid="product-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Costo</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={productForm.cost}
                    onChange={(e) => setProductForm({ ...productForm, cost: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <select
                    value={productForm.category_id}
                    onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="product-category-select"
                  >
                    <option value="">Seleccionar categoría</option>
                    {flatCategories.map(cat => (
                      <option key={cat.category_id} value={cat.category_id}>
                        {cat.parent_id ? `↳ ${cat.name}` : cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Stock Inicial</Label>
                  <Input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
              
              {/* Deduction Area Selection */}
              <div className="space-y-2">
                <Label>Área de Descuento de Ingredientes</Label>
                <select
                  value={productForm.deduction_area_id}
                  onChange={(e) => setProductForm({ ...productForm, deduction_area_id: e.target.value })}
                  className="w-full border rounded-md p-2"
                  data-testid="deduction-area-select"
                >
                  <option value="">Cocina (por defecto)</option>
                  {areas.map(area => (
                    <option key={area.area_id} value={area.area_id}>
                      {area.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Selecciona de qué área se descontarán los ingredientes al vender este producto.
                  Ej: Bebidas desde "Barra", Platillos desde "Cocina".
                </p>
              </div>
              
              {/* Kitchen Station Selection - For routing to kitchen monitor */}
              <div className="space-y-2">
                <Label>Estación de Cocina (Monitor)</Label>
                <select
                  value={productForm.kitchen_station}
                  onChange={(e) => setProductForm({ ...productForm, kitchen_station: e.target.value })}
                  className="w-full border rounded-md p-2"
                  data-testid="kitchen-station-select"
                >
                  <option value="">Sin asignar</option>
                  <option value="caliente">🔥 Cocina Caliente</option>
                  <option value="fria">🧊 Cocina Fría</option>
                </select>
                <p className="text-xs text-gray-500">
                  Selecciona a qué estación del monitor de cocina se enviará este producto.
                  Caliente: filetes, tacos, etc. Fría: cocteles, tostadas, etc.
                </p>
              </div>
              
              {/* Sold by Weight Toggle */}
              <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold">Se vende por peso</Label>
                    <p className="text-xs text-gray-500">
                      Activa si el producto se vende por kg, gramos, etc.
                    </p>
                  </div>
                  <Switch
                    checked={productForm.sold_by_weight}
                    onCheckedChange={(v) => setProductForm({ ...productForm, sold_by_weight: v })}
                    data-testid="sold-by-weight-switch"
                  />
                </div>
                
                {productForm.sold_by_weight && (
                  <div className="space-y-2 pt-2 border-t border-blue-200">
                    <Label>Unidad de peso</Label>
                    <select
                      value={productForm.weight_unit}
                      onChange={(e) => setProductForm({ ...productForm, weight_unit: e.target.value })}
                      className="w-full border rounded-md p-2"
                      data-testid="weight-unit-select"
                    >
                      <option value="kg">Kilogramos (kg)</option>
                      <option value="g">Gramos (g)</option>
                      <option value="lb">Libras (lb)</option>
                    </select>
                    <p className="text-xs text-blue-600 font-medium">
                      El precio ${productForm.price || '0'} será por {productForm.weight_unit || 'kg'}. 
                      El mesero podrá ingresar la cantidad (ej: 0.5, 1, 1.5).
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <Label>Disponible para venta</Label>
                <Switch
                  checked={productForm.is_available}
                  onCheckedChange={(v) => setProductForm({ ...productForm, is_available: v })}
                />
              </div>

              {/* Digital Menu Section */}
              <div className="border-t pt-4 mt-4 space-y-3">
                <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  📱 Menú Digital
                </h3>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Visible en Menú Digital</Label>
                    <p className="text-xs text-muted-foreground">Aparece en el menú público para clientes</p>
                  </div>
                  <Switch
                    checked={productForm.visible_in_menu}
                    onCheckedChange={(v) => setProductForm({ ...productForm, visible_in_menu: v })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Producto Popular ⭐</Label>
                    <p className="text-xs text-muted-foreground">Se destaca con etiqueta "Popular"</p>
                  </div>
                  <Switch
                    checked={productForm.is_popular}
                    onCheckedChange={(v) => setProductForm({ ...productForm, is_popular: v })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div>
                    <Label className="text-purple-700">🏷️ Solo para Promociones</Label>
                    <p className="text-xs text-purple-600">NO aparece en menú normal, solo en promociones</p>
                  </div>
                  <Switch
                    checked={productForm.promo_only}
                    onCheckedChange={(v) => setProductForm({ ...productForm, promo_only: v, visible_in_menu: v ? false : productForm.visible_in_menu })}
                  />
                </div>

                {/* Grupos de Extras */}
                {extraGroups.length > 0 && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 space-y-2">
                    <Label className="text-orange-700 font-medium">🧂 Grupos de Extras</Label>
                    <p className="text-xs text-orange-600 mb-2">Selecciona qué extras puede agregar el cliente a este producto</p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {extraGroups.map(group => (
                        <label key={group.group_id} className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:bg-orange-50">
                          <input
                            type="checkbox"
                            checked={productForm.extra_group_ids?.includes(group.group_id)}
                            onChange={(e) => {
                              const currentIds = productForm.extra_group_ids || [];
                              if (e.target.checked) {
                                setProductForm({ ...productForm, extra_group_ids: [...currentIds, group.group_id] });
                              } else {
                                setProductForm({ ...productForm, extra_group_ids: currentIds.filter(id => id !== group.group_id) });
                              }
                            }}
                            className="rounded border-orange-300 text-orange-500 focus:ring-orange-500"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-700">{group.name}</span>
                            <span className="text-xs text-gray-400 ml-2">({group.extras?.length || 0} extras)</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Elaboration Details Section */}
              <div className="border-t pt-4 mt-4 space-y-4">
                <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <ChefHat className="w-4 h-4" />
                  Detalles de Preparación (Opcional)
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Tiempo Preparación
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={productForm.prep_time_minutes}
                        onChange={(e) => setProductForm({ ...productForm, prep_time_minutes: e.target.value })}
                        placeholder="15"
                      />
                      <span className="text-sm text-muted-foreground">min</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Tiempo Cocción
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={productForm.cook_time_minutes}
                        onChange={(e) => setProductForm({ ...productForm, cook_time_minutes: e.target.value })}
                        placeholder="20"
                      />
                      <span className="text-sm text-muted-foreground">min</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Presentación</Label>
                  <Input
                    value={productForm.presentation}
                    onChange={(e) => setProductForm({ ...productForm, presentation: e.target.value })}
                    placeholder="Ej: Servir en plato hondo con limones y salsa..."
                  />
                </div>
                
                {/* Preparation Steps */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-1">
                      <ListOrdered className="w-3 h-3" /> Procedimiento ({productForm.preparation_steps.length} pasos)
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addProductStep}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar Paso
                    </Button>
                  </div>
                  
                  {productForm.preparation_steps.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-3 bg-gray-50 rounded-lg">
                      Sin pasos de preparación. Clic en "Agregar Paso" para comenzar.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {productForm.preparation_steps.map((step, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                          <span className="w-5 h-5 rounded-full bg-accent text-white text-xs flex items-center justify-center flex-shrink-0 mt-1">
                            {step.step_number}
                          </span>
                          <div className="flex-1">
                            <Input
                              value={step.description}
                              onChange={(e) => updateProductStep(idx, 'description', e.target.value)}
                              placeholder="Describe este paso..."
                              className="text-sm h-8"
                            />
                          </div>
                          <Input
                            type="number"
                            value={step.duration_minutes || ''}
                            onChange={(e) => updateProductStep(idx, 'duration_minutes', e.target.value ? parseInt(e.target.value) : null)}
                            placeholder="min"
                            className="w-16 text-sm h-8"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive flex-shrink-0"
                            onClick={() => removeProductStep(idx)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Selectable Ingredients Section */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={productForm.has_selectable_ingredients}
                        onCheckedChange={(checked) => setProductForm({ ...productForm, has_selectable_ingredients: checked })}
                        id="has-selectable"
                      />
                      <Label htmlFor="has-selectable" className="flex items-center gap-2 cursor-pointer">
                        <Layers className="w-4 h-4 text-purple-500" />
                        <span className="font-medium">Ingredientes Seleccionables</span>
                      </Label>
                    </div>
                  </div>
                  {productForm.has_selectable_ingredients && (
                    <span className="text-xs text-muted-foreground bg-purple-50 px-2 py-1 rounded">
                      El cliente puede elegir qué ingredientes incluir
                    </span>
                  )}
                </div>
                
                {productForm.has_selectable_ingredients && (
                  <div className="space-y-4 p-4 bg-purple-50/50 rounded-lg border border-purple-200">
                    {/* Total Weight */}
                    <div className="flex items-center gap-4">
                      <div className="flex-1 space-y-1">
                        <Label className="flex items-center gap-1 text-sm">
                          <Scale className="w-3 h-3" /> Peso Total del Producto
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={productForm.total_weight_grams}
                            onChange={(e) => setProductForm({ ...productForm, total_weight_grams: e.target.value })}
                            placeholder="200"
                            className="w-24"
                          />
                          <span className="text-sm text-muted-foreground">gramos</span>
                          {productForm.selectable_ingredients.length > 0 && productForm.total_weight_grams && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={recalculateBaseQuantities}
                            >
                              Redistribuir
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Ingredientes: {productForm.selectable_ingredients.length}</p>
                        {productForm.total_weight_grams && productForm.selectable_ingredients.length > 0 && (
                          <p className="text-xs text-purple-600">
                            ~{Math.round(parseFloat(productForm.total_weight_grams) / productForm.selectable_ingredients.length)}g c/u
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Add Ingredient */}
                    <div className="space-y-2">
                      <Label className="text-sm">Agregar Insumo Elaborado</Label>
                      <SearchSelect
                        options={processedSupplies.map(p => ({ value: p.processed_id, label: p.name, data: p }))}
                        placeholder="Buscar insumo elaborado (ej: Camarón, Pulpo...)"
                        onSelect={(opt) => addSelectableIngredient(opt.data)}
                      />
                    </div>
                    
                    {/* Ingredients List */}
                    {productForm.selectable_ingredients.length === 0 ? (
                      <p className="text-sm text-center text-muted-foreground py-4">
                        Agrega los insumos elaborados que el cliente puede seleccionar
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <Label className="text-sm">Ingredientes Configurados</Label>
                        <div className="grid gap-2">
                          {productForm.selectable_ingredients.map((ing) => (
                            <div key={ing.processed_id} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                              <div className="flex-1">
                                <p className="font-medium text-sm">{ing.name}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  value={ing.base_quantity}
                                  onChange={(e) => updateSelectableIngredient(ing.processed_id, 'base_quantity', parseFloat(e.target.value) || 0)}
                                  className="w-20 text-sm h-8"
                                />
                                <span className="text-xs text-muted-foreground">g</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={ing.is_default}
                                    onChange={(e) => updateSelectableIngredient(ing.processed_id, 'is_default', e.target.checked)}
                                    className="rounded"
                                  />
                                  Por defecto
                                </label>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => removeSelectableIngredient(ing.processed_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        
                        {/* Preview with Cost Calculation */}
                        <div className="mt-3 p-3 bg-white rounded-lg border border-dashed">
                          <p className="text-xs text-muted-foreground mb-2">Vista previa en POS:</p>
                          <p className="text-sm font-medium mb-1">{productForm.name || 'Producto'}</p>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {productForm.selectable_ingredients.map(ing => (
                              <span key={ing.processed_id} className={`text-xs px-2 py-1 rounded-full ${ing.is_default ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {ing.is_default ? '✓' : '○'} {ing.name}
                              </span>
                            ))}
                          </div>
                          
                          {/* Cost Calculation Preview */}
                          {productForm.selectable_ingredients.length > 0 && (
                            <div className="pt-2 border-t">
                              <p className="text-xs text-muted-foreground mb-1">Costo estimado de ingredientes:</p>
                              <div className="space-y-1">
                                {productForm.selectable_ingredients.map(ing => {
                                  const ps = processedSupplies.find(p => p.processed_id === ing.processed_id);
                                  const costPerKg = ps?.cost_per_unit || 0;
                                  const qtyKg = (ing.base_quantity || 0) / 1000;
                                  const cost = costPerKg * qtyKg;
                                  return (
                                    <div key={ing.processed_id} className="flex justify-between text-xs">
                                      <span>{ing.name} ({ing.base_quantity || 0}g)</span>
                                      <span className="text-muted-foreground">${cost.toFixed(2)}</span>
                                    </div>
                                  );
                                })}
                                <div className="flex justify-between text-sm font-bold pt-1 border-t">
                                  <span>Costo Total:</span>
                                  <span className="text-green-600">
                                    ${productForm.selectable_ingredients.reduce((sum, ing) => {
                                      const ps = processedSupplies.find(p => p.processed_id === ing.processed_id);
                                      const costPerKg = ps?.cost_per_unit || 0;
                                      const qtyKg = (ing.base_quantity || 0) / 1000;
                                      return sum + (costPerKg * qtyKg);
                                    }, 0).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="flex-1 bg-accent text-white hover:bg-accent/90" data-testid="save-product-btn">
              {editingProduct ? 'Actualizar' : 'Crear'} Producto
            </Button>
            {editingProduct && (
              <Button 
                type="button" 
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => handleDownloadProductPdf(editingProduct)}
                title="Descargar ficha PDF"
              >
                <FileDown className="w-4 h-4" />
                PDF
              </Button>
            )}
          </div>
        </form>
      </Modal>

      {/* Recipe Modal */}
      <Modal
        open={recipeDialogOpen}
        onClose={() => setRecipeDialogOpen(false)}
        title={`Receta: ${recipeProduct?.name || ''}`}
        wide
      >
        <form onSubmit={handleRecipeSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Define los insumos (básicos o elaborados) que componen este producto.
          </p>
          
          {/* Ingredients List */}
          <div className="space-y-3">
            {recipeIngredients.map((ing, idx) => {
              // Find supply (basic or processed)
              const basicSupply = supplies.find(s => s.supply_id === ing.supply_id);
              const processedSupply = processedSupplies.find(p => p.processed_id === ing.processed_id);
              const currentSupply = basicSupply || processedSupply;
              const costPerUnit = currentSupply?.cost_per_unit || 0;
              const ingredientCost = currentSupply && ing.quantity ? (costPerUnit * parseFloat(ing.quantity)) : 0;
              const isProcessed = !!processedSupply;
              
              return (
                <div key={idx} className="p-3 bg-muted/20 rounded-lg">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Tipo de Insumo</Label>
                      <select
                        value={ing.type || 'basic'}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const updated = [...recipeIngredients];
                          updated[idx] = { type: newType, supply_id: '', processed_id: '', quantity: ing.quantity };
                          setRecipeIngredients(updated);
                        }}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mb-2"
                      >
                        <option value="basic">Insumo Básico</option>
                        <option value="processed">Insumo Elaborado</option>
                      </select>
                      
                      {(ing.type || 'basic') === 'basic' ? (
                        <SearchSelect
                          options={supplies.map(s => ({
                            ...s,
                            displayName: `${s.name} - $${s.cost_per_unit}/${s.unit}`
                          }))}
                          value={ing.supply_id}
                          onChange={(val) => updateIngredient(idx, 'supply_id', val)}
                          placeholder="Buscar insumo básico..."
                          displayKey="displayName"
                          valueKey="supply_id"
                          renderOption={(opt) => (
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{opt.name}</span>
                              <span className="text-muted-foreground text-xs">
                                ${opt.cost_per_unit}/{opt.unit}
                              </span>
                            </div>
                          )}
                        />
                      ) : (
                        <SearchSelect
                          options={processedSupplies.map(p => ({
                            ...p,
                            displayName: `${p.name} - $${p.cost_per_unit?.toFixed(2)}/${p.unit}`
                          }))}
                          value={ing.processed_id || ''}
                          onChange={(val) => updateIngredient(idx, 'processed_id', val)}
                          placeholder="Buscar elaborado..."
                          displayKey="displayName"
                          valueKey="processed_id"
                          renderOption={(opt) => (
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{opt.name}</span>
                              <span className="text-muted-foreground text-xs">
                                ${opt.cost_per_unit?.toFixed(2)}/{opt.unit}
                              </span>
                            </div>
                          )}
                        />
                      )}
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={ing.quantity}
                        onChange={(e) => updateIngredient(idx, 'quantity', e.target.value)}
                        placeholder={currentSupply?.unit || '0'}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-destructive"
                      onClick={() => removeIngredient(idx)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Show cost per ingredient */}
                  {currentSupply && ing.quantity && (
                    <div className="mt-2 pt-2 border-t border-border/50 flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {ing.quantity} {currentSupply.unit} × ${costPerUnit.toFixed(2)}
                        {isProcessed && <span className="ml-1 text-accent">(elaborado)</span>}
                      </span>
                      <span className="font-bold text-accent">
                        = ${ingredientCost.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={addIngredientRow}
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Insumo
          </Button>
          
          {/* Cost Summary */}
          {recipeIngredients.length > 0 && (
            <div className="p-4 bg-muted/30 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Costo Total de Insumos:</span>
                <span className="text-xl font-chivo font-extrabold text-accent">
                  ${calculateRecipeCost().toFixed(2)}
                </span>
              </div>
              {recipeProduct && (
                <div className="flex justify-between items-center mt-2 text-sm text-muted-foreground">
                  <span>Precio de venta: ${recipeProduct.price}</span>
                  <span>Margen: {Math.round(((recipeProduct.price - calculateRecipeCost()) / recipeProduct.price) * 100)}%</span>
                </div>
              )}
            </div>
          )}
          
          {supplies.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay insumos registrados. Ve a <strong>Insumos</strong> para crear algunos primero.
            </p>
          )}
          
          <Button 
            type="submit" 
            className="w-full bg-accent text-white hover:bg-accent/90"
            disabled={recipeIngredients.length === 0}
          >
            Guardar Receta
          </Button>
        </form>
      </Modal>
    </div>
  );
};

export default Products;
